import { ReactNode, useEffect, useRef } from 'react';

import {
  Box,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  StackDivider,
  Text,
  useColorModeValue,
} from '@ecoinvent/ui';
import mixpanel from 'mixpanel-browser';

import { MIXPANEL_EVENTS } from 'utilities/constants/mixpanel';
import { formatNumericalValue } from 'utilities/numbers';

import { ComparedIndicator } from './comparisonUtils';
import {
  buildSegments,
  computeChange,
  ContributionSegment,
  CONTRIBUTOR_COLORS_DARK,
  CONTRIBUTOR_COLORS_LIGHT,
  formatChange,
  hatchBackgroundImage,
  NO_BREAKDOWN_BG,
  NO_BREAKDOWN_HATCH,
} from './impactUtils';

export const IMPACT_GRID_COLUMNS = 'minmax(220px, 1fr) 110px 90px 70px';

const ROW_HEIGHT = '28px';

const formatAmount = (amount?: number) => (amount === undefined ? '—' : formatNumericalValue(amount));

const ContributionBar = ({ widthPct, segments }: { widthPct: number; segments: ContributionSegment[] }) => {
  const noBreakdownBg = useColorModeValue(NO_BREAKDOWN_BG.light, NO_BREAKDOWN_BG.dark);
  const noBreakdownHatch = useColorModeValue(NO_BREAKDOWN_HATCH.light, NO_BREAKDOWN_HATCH.dark);

  const hasSegments = segments.length > 0;
  const isEmptyBar = widthPct === 0;

  return (
    <Box flex={1} minW={0}>
      <Box width={`${widthPct}%`} minW={isEmptyBar ? 0 : '6px'}>
        {hasSegments ? (
          <Flex h={ROW_HEIGHT} gap="2px">
            {/* Magnitude, not the signed share: flex-grow treats a negative value as 0. */}
            {segments.map((segment) => (
              <Box
                key={segment.key}
                flexGrow={Math.abs(segment.share)}
                flexBasis={0}
                minW="2px"
                borderRadius="sm"
                bg={segment.color}
              />
            ))}
          </Flex>
        ) : (
          <Box
            h={ROW_HEIGHT}
            borderRadius="sm"
            bg={isEmptyBar ? 'transparent' : noBreakdownBg}
            backgroundImage={isEmptyBar ? undefined : hatchBackgroundImage(noBreakdownHatch)}
          />
        )}
      </Box>
    </Box>
  );
};

const CONTRIBUTOR_GRID_COLUMNS = '1fr 72px 90px';

const ContributorLine = ({ segment }: { segment: ContributionSegment }) => {
  const grayText = useColorModeValue('gray.600', 'gray.400');

  return (
    <Grid templateColumns={CONTRIBUTOR_GRID_COLUMNS} columnGap={3} fontSize="sm" alignItems="center">
      <Text isTruncated title={segment.label}>
        {segment.label}
      </Text>
      <Text color={grayText} textAlign="right" whiteSpace="nowrap">
        {(segment.share * 100).toFixed(1)}%
      </Text>
      <Text textAlign="right">{formatNumericalValue(segment.impact)}</Text>
    </Grid>
  );
};

type DatasetSide = { label: string; amount?: number; segments: ContributionSegment[] };

type ContributorsPopoverProps = {
  side: DatasetSide;
  indicator: ComparedIndicator;
  placement: 'top' | 'bottom';
  onOpen: () => void;
  onClose: () => void;
  children: ReactNode;
};

const ContributorsPopover = ({ side, indicator, placement, onOpen, onClose, children }: ContributorsPopoverProps) => {
  const grayText = useColorModeValue('gray.600', 'gray.400');
  const subGray = useColorModeValue('gray.500', 'gray.500');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.200');
  const headerColor = useColorModeValue('gray.600', 'gray.400');

  return (
    <Popover trigger="hover" placement={placement} isLazy onOpen={onOpen} onClose={onClose}>
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent w="420px" maxW="90vw">
        <PopoverArrow />
        <PopoverBody p={4}>
          <Stack spacing={0} mb={3}>
            <Text fontSize="xs" fontWeight="semibold" color={subGray}>
              {side.label}
            </Text>
            <Heading size="sm">{indicator.category}</Heading>
            <Text fontSize="sm" fontWeight="medium" color={grayText}>
              {indicator.indicator}
            </Text>
          </Stack>
          {side.segments.length > 0 ? (
            <Stack spacing={1.5}>
              <Grid
                templateColumns={CONTRIBUTOR_GRID_COLUMNS}
                columnGap={3}
                fontSize="xs"
                letterSpacing="wider"
                textTransform="uppercase"
                color={headerColor}
                pl="15px"
                pb={1.5}
              >
                <Text>Contributor</Text>
                <Text textAlign="right">Share</Text>
                <Text textAlign="right" fontWeight="semibold">
                  Impact ({indicator.unit})
                </Text>
              </Grid>
              <Stack spacing={1.5} divider={<StackDivider borderColor={borderColor} />}>
                {side.segments.map((segment) => (
                  <HStack key={segment.key} align="stretch" spacing={3}>
                    <Box w="3px" flexShrink={0} borderRadius="full" bg={segment.color} />
                    <Box flex={1} minW={0}>
                      <ContributorLine segment={segment} />
                    </Box>
                  </HStack>
                ))}
              </Stack>
            </Stack>
          ) : (
            <Text fontSize="xs" color={subGray}>
              {side.label} has no contributor breakdown for this category.
            </Text>
          )}
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const CONTRIBUTORS_VIEWED_DELAY_MS = 500;

type Props = {
  indicator: ComparedIndicator;
  datasetIds: string[];
  lciaMethod?: string;
};

const ImpactCategoryRow = ({ indicator, datasetIds, lciaMethod }: Props) => {
  const grayText = useColorModeValue('gray.600', 'gray.400');
  const subGray = useColorModeValue('gray.500', 'gray.500');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.200');
  const contributorColors = useColorModeValue(CONTRIBUTOR_COLORS_LIGHT, CONTRIBUTOR_COLORS_DARK);

  const [amountA, amountB] = indicator.amounts;
  const maxAmount = Math.max(Math.abs(amountA ?? 0), Math.abs(amountB ?? 0));
  const widthPct = (amount?: number) => (maxAmount > 0 && amount !== undefined ? (Math.abs(amount) / maxAmount) * 100 : 0);

  const changeText = formatChange(computeChange(amountA, amountB));

  const sides: DatasetSide[] = [
    { label: 'ds1', amount: amountA, segments: buildSegments(indicator.contributions[0], contributorColors) },
    { label: 'ds2', amount: amountB, segments: buildSegments(indicator.contributions[1], contributorColors) },
  ];

  const viewedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearViewedTimer = () => {
    if (viewedTimerRef.current) {
      clearTimeout(viewedTimerRef.current);
      viewedTimerRef.current = null;
    }
  };

  useEffect(() => clearViewedTimer, []);

  const onContributorsOpen = (index: number) => {
    clearViewedTimer();
    viewedTimerRef.current = setTimeout(() => {
      mixpanel.track(MIXPANEL_EVENTS.COMPARISON_CONTRIBUTORS_VIEWED, {
        category: indicator.category,
        indicator: indicator.indicator,
        lcia_method: lciaMethod,
        dataset_ids: datasetIds,
        dataset_id: datasetIds[index],
        has_contributors: sides[index].segments.length > 0,
      });
    }, CONTRIBUTORS_VIEWED_DELAY_MS);
  };

  return (
    <Box py={4} borderBottomWidth="1px" borderColor={borderColor}>
      <Stack spacing={0} mb={3}>
        <Heading size="sm">{indicator.category}</Heading>
        <Text fontSize="sm" fontWeight="medium" color={grayText}>
          {indicator.indicator}
        </Text>
      </Stack>

      <Grid templateColumns={IMPACT_GRID_COLUMNS} columnGap={6} alignItems="center">
        <GridItem>
          <Stack spacing={2}>
            {sides.map((side, index) => {
              const bar = (
                <HStack key={side.label} spacing={2} minH={ROW_HEIGHT}>
                  <Text w="30px" fontSize="xs" fontWeight="medium" color={subGray}>
                    {side.label}
                  </Text>
                  <ContributionBar widthPct={widthPct(side.amount)} segments={side.segments} />
                </HStack>
              );

              return side.amount === undefined ? (
                bar
              ) : (
                <ContributorsPopover
                  key={side.label}
                  side={side}
                  indicator={indicator}
                  placement={index === 0 ? 'top' : 'bottom'}
                  onOpen={() => onContributorsOpen(index)}
                  onClose={clearViewedTimer}
                >
                  {bar}
                </ContributorsPopover>
              );
            })}
          </Stack>
        </GridItem>
        <GridItem>
          <Stack spacing={2}>
            <Text fontSize="sm" fontWeight="medium" h={ROW_HEIGHT} lineHeight={ROW_HEIGHT}>
              {formatAmount(amountA)}
            </Text>
            <Text fontSize="sm" fontWeight="medium" h={ROW_HEIGHT} lineHeight={ROW_HEIGHT}>
              {formatAmount(amountB)}
            </Text>
          </Stack>
        </GridItem>
        <GridItem>
          <Text fontSize="sm" color={grayText} wordBreak="break-word">
            {indicator.unit}
          </Text>
        </GridItem>
        <GridItem>
          <Text fontSize="sm" fontWeight="semibold">
            {changeText}
          </Text>
        </GridItem>
      </Grid>
    </Box>
  );
};

export default ImpactCategoryRow;
